import React, { ReactElement, useEffect, useMemo } from 'react'

import { Col, Modal, Row } from 'react-bootstrap'
import { Geocoding, Button, Paragraph, ListCategories, Loading } from 'src/components'
import ClientIcon from 'src/assets/images/svg/client.svg'

import { formatDateFromMillis } from 'src/utils/formatters'
import { useDispatch, useSelector } from 'react-redux'
import { selectIdentificationTypes } from 'src/selectors/company.selector'
import { faPhone } from '@fortawesome/free-solid-svg-icons'

import { actions, getClientDetail } from 'src/actions/clientOpt.actions'
import { selectClientDetail } from 'src/selectors/clientOpt.selector'
import { loadingSelector } from 'src/selectors/loading.selector'

interface OrderClientDetailProps {
  clientId: number | undefined
  onHide: () => void
}

const fieldMapper = (client: IClientDetail, identificationTypes): ISelect[] => {
  if (!client) return []

  const fields: ISelect[] = []
  fields.push({
    label: 'Código',
    value: client.code || 'Desconocido',
  })
  fields.push({
    label: 'Teléfono',
    value: client.phone || '-',
  })

  if (client.birthDate)
    fields.push({
      label: 'Fecha de cumpleaños',
      value: formatDateFromMillis(client.birthDate),
    })

  fields.push({
    label: 'Dirección',
    value: client.address || '-',
  })
  fields.push({
    label: 'Referencia',
    value: client.reference || '-',
  })

  identificationTypes?.map(identificationTypes => {
    fields.push({
      label: identificationTypes.name,
      value:
        client.identifications?.find(
          identification => identification.id === identificationTypes.id,
        )?.value || '-',
    })
  })

  return fields
}

const isValidCc = (value: string): boolean => {
  const numberValue = Number(value)

  return !isNaN(numberValue) && numberValue !== 0
}

const LoadingContent = ({
  loading,
  children,
}: {
  children: ReactElement
  loading: boolean
}): ReactElement => {
  if (loading) return <Loading show />
  return children
}

export const SimpleClientDetail = ({ clientId, onHide }: OrderClientDetailProps) => {
  const show = Boolean(clientId)

  const dispatch = useDispatch()

  const identificationTypes = useSelector(selectIdentificationTypes)

  const client: IClientDetail = useSelector(selectClientDetail)
  const loading: boolean = useSelector(state => loadingSelector([actions.detail])(state))

  useEffect(() => {
    if (!show) return
    dispatch(getClientDetail(clientId))
  }, [show])

  const onClose = () => {
    onHide()
  }

  const categories: ISelect[] = useMemo(() => {
    if (!show) return []
    return (
      client?.categories?.map(c => ({
        label: c.value,
        value: c.id,
      })) ?? []
    )
  }, [show, client, loading])

  const fields: ISelect[] = useMemo(() => {
    if (!show) return []
    return fieldMapper(client, identificationTypes)
  }, [show, client, identificationTypes, loading])

  return (
    <div>
      <Modal show={show} centered onHide={onClose}>
        <Modal.Header closeButton>
          <Modal.Title>{loading ? 'Cargando' : client.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <LoadingContent loading={loading}>
            <Row className={'custom-card'}>
              <Col xl={12}>
                <Row style={{ justifyContent: 'center' }}>
                  <img
                    className={'picture-client'}
                    src={client.img || ClientIcon}
                    alt={client.name}
                  />
                </Row>
              </Col>

              {client.phone && (
                <Col xl={12}>
                  <Row style={{ justifyContent: 'center' }}>
                    <Button
                      icon={faPhone}
                      onClick={() => {
                        window.location.href = `tel:${client.phone}`
                      }}>
                      Llamar cliente
                    </Button>
                  </Row>
                </Col>
              )}

              {fields.map(field => (
                <Col xl={12} key={field.label} className={'space-between'}>
                  <Paragraph>{field.label}</Paragraph>
                  <Paragraph>{field.value}</Paragraph>
                </Col>
              ))}

              <Col xl={12} md={12} sm={12}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'flex-start',
                    flexFlow: 'wrap',
                  }}>
                  <ListCategories items={categories} label={'Categorías'} />
                </div>
              </Col>

              {isValidCc(client.latitude) && isValidCc(client.longitude) && (
                <Col xl={12} md={12} xs={12}>
                  <Geocoding
                    lat={client.latitude}
                    lng={client.longitude}
                    defaultAddress={client.address}
                    auto={false}
                    editable={false}
                    getData={() => undefined}
                    noEdit
                  />
                </Col>
              )}
            </Row>
          </LoadingContent>
        </Modal.Body>
      </Modal>
    </div>
  )
}
